import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChevronDown, ChevronUp, ExternalLink, Info, Lock, RefreshCw, Search, X } from 'lucide-react';
import { apiClient } from '../api';
import { useAssistantRecord, useChat } from '../contexts/ChatContext';
import { errMessage } from '../components/UI/ink';
import { useAuth } from '../auth/AuthProvider';
import { useVerdicts } from '../hooks/useVerdicts';
import { usePrivateIocs } from '../hooks/usePrivateIocs';
import VerdictBadge from '../components/Triage/VerdictBadge';
import AlertDetail, { SevChip } from '../components/SecurityAlerts/AlertDetail';
import CreateCaseDrawer from '../components/SecurityAlerts/CreateCaseDrawer';
import PrivateIndicatorsDrawer from '../components/SecurityAlerts/PrivateIndicatorsDrawer';
import { SEVS, SEV_LABEL, SORTS, SORT_LABEL, ago, fromCve, fromIoc, isPrivateAlert, sevFg } from '../components/SecurityAlerts/model';

// One inbox for IoC matches and vulnerabilities, sorted by severity then
// time, with the selected alert in a docked pane (same pattern as the
// Network Map inspector). Selection lives in ?alert=ioc-12 so it can be linked;
// the private-indicators drawer lives in ?private=1.
const RESOLUTION = [['0', 'Unresolved'], ['1', 'Resolved'], ['all', 'All']];
const SOURCES = [['all', 'All'], ['ioc', 'IoC matches'], ['private', 'Private'], ['cve', 'Vulnerabilities']];

const seg = (on) => ({
  background: on ? 'var(--surf-raised)' : 'transparent',
  borderColor: on ? 'var(--surf-line)' : 'transparent',
  color: on ? 'var(--ink-text)' : 'var(--ink-muted)',
});
const selectStyle = { background: 'var(--surf-surface)', borderColor: 'var(--surf-line)', color: 'var(--ink-text)' };

const SecurityAlerts = () => {
  const { openChatWithAlert } = useChat();
  const [searchParams, setSearchParams] = useSearchParams();
  const [servers, setServers] = useState([]);
  const [iocAlerts, setIocAlerts] = useState([]);
  const [cveAlerts, setCveAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState(null);
  const [resolved, setResolved] = useState('0');
  const [serverId, setServerId] = useState('all');
  const [source, setSource] = useState('all');
  const [sevs, setSevs] = useState([]);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('severity');
  const [checked, setChecked] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [nvd, setNvd] = useState(null);
  const [nvdOpen, setNvdOpen] = useState(false);
  const [caseItems, setCaseItems] = useState(null);
  const priv = usePrivateIocs();

  const selectedKey = searchParams.get('alert');
  const select = (key) => {
    const next = new URLSearchParams(searchParams);
    if (key) next.set('alert', key); else next.delete('alert');
    setSearchParams(next, { replace: true });
  };

  const privateOpen = searchParams.get('private') === '1';
  const setPrivateOpen = (on) => {
    const next = new URLSearchParams(searchParams);
    if (on) next.set('private', '1'); else next.delete('private');
    setSearchParams(next, { replace: true });
  };

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (resolved !== 'all') params.set('resolved', resolved);
    if (serverId !== 'all') params.set('server_id', serverId);
    const [ioc, cve] = await Promise.allSettled([
      apiClient.get(`/api/alerts/ioc?${params}`),
      apiClient.get(`/api/alerts/cve?${params}`),
    ]);
    if (ioc.status === 'fulfilled') setIocAlerts(ioc.value.data.ioc_alerts || []);
    if (cve.status === 'fulfilled') setCveAlerts(cve.value.data.cve_alerts || []);
    const failed = [ioc, cve].find(r => r.status === 'rejected');
    if (failed) setNotice({ kind: 'error', text: errMessage(failed.reason, 'Failed to load some alerts.') });
    setChecked(new Set());
    setLoading(false);
  }, [resolved, serverId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    apiClient.get('/api/servers?state=REGISTERED').then(r => setServers(r.data.servers || [])).catch(() => {});
    apiClient.get('/api/vulns/nvd-feed').then(r => setNvd(r.data)).catch(() => {});
  }, []);

  const hostOf = useCallback((id) => servers.find(s => s.id === id)?.hostname || id, [servers]);

  const items = useMemo(
    () => [...iocAlerts.map(a => fromIoc(a, hostOf)), ...cveAlerts.map(a => fromCve(a, hostOf))],
    [iocAlerts, cveAlerts, hostOf],
  );
  const byKey = useMemo(() => Object.fromEntries(items.map(i => [i.key, i])), [items]);
  const { permissions = [] } = useAuth();
  const canRate = permissions.includes('events:update');
  const canManage = permissions.includes('system:update');
  const verdictKeys = useMemo(() => items.map(i => `${i.kind}:${i.id}`), [items]);
  const { verdicts, setVerdict } = useVerdicts(verdictKeys);

  // How many assets share each indicator value.
  const iocSpread = useMemo(() => {
    const m = {};
    iocAlerts.forEach(a => { (m[a.ioc_value] = m[a.ioc_value] || new Set()).add(a.server_id); });
    return m;
  }, [iocAlerts]);

  const q = search.trim().toLowerCase();
  const inSource = (i) => source === 'all' || (source === 'private' ? i.kind === 'ioc' && isPrivateAlert(i.raw) : i.kind === source);
  const visible = useMemo(() => items
    .filter(i => inSource(i) && (sevs.length === 0 || sevs.includes(i.sev)) && (!q || i.search.includes(q) || i.title.toLowerCase().includes(q)))
    .sort(SORTS[sort]), [items, source, sevs, q, sort]); // eslint-disable-line react-hooks/exhaustive-deps

  const selected = selectedKey ? byKey[selectedKey] || null : null;
  useAssistantRecord(selected && { kind: selected.kind, id: String(selected.id), label: `#${selected.id}` });
  const fpItems = items.filter(i => i.fp);
  const checkedItems = items.filter(i => checked.has(i.key));
  const allChecked = visible.length > 0 && visible.every(v => checked.has(v.key));

  const toggle = (key) => setChecked(prev => { const n = new Set(prev); if (n.has(key)) n.delete(key); else n.add(key); return n; });
  const toggleAll = () => setChecked(allChecked ? new Set() : new Set(visible.map(v => v.key)));

  const resolveItems = async (list) => {
    setBusy(true);
    const results = await Promise.allSettled(list.map(i => apiClient.patch(`/api/alerts/${i.kind}/${i.id}`)));
    const failed = results.filter(r => r.status === 'rejected').length;
    const done = list.length - failed;
    setNotice(failed
      ? { kind: 'error', text: `Resolved ${done} of ${list.length}. ${failed} failed; try again.` }
      : { kind: 'ok', text: `${done} alert${done === 1 ? '' : 's'} resolved.` });
    if (list.some(i => i.key === selectedKey) && resolved === '0') select(null);
    setBusy(false);
    load();
  };

  const resolvePrivate = async () => {
    setBusy(true);
    try {
      await apiClient.post('/api/alerts/ioc/resolve-private-ips');
      setNotice({ kind: 'ok', text: `${fpItems.length} private-IP alert${fpItems.length === 1 ? '' : 's'} resolved as false positives.` });
      load();
    } catch (err) {
      setNotice({ kind: 'error', text: errMessage(err, 'Failed to resolve private IP alerts.') });
    } finally {
      setBusy(false);
    }
  };

  const counts = useMemo(() => {
    const c = { all: items.length, ioc: 0, cve: 0, private: 0 };
    items.forEach(i => { c[i.kind] += 1; if (i.kind === 'ioc' && isPrivateAlert(i.raw)) c.private += 1; if (inSource(i)) c[i.sev] = (c[i.sev] || 0) + 1; });
    return c;
  }, [items, source]); // eslint-disable-line react-hooks/exhaustive-deps

  const nvdIds = useMemo(() => new Set((nvd?.high_critical || []).map(n => n.cve_id)), [nvd]);
  const nvdMatches = cveAlerts.filter(a => nvdIds.has(a.cve_id)).length;
  const resLabel = { '0': 'unresolved', '1': 'resolved', all: 'total' }[resolved];

  return (
    <div className="p-6 lg:p-8 flex flex-col gap-[18px]" style={{ color: 'var(--ink-text)' }}>
      {/* Title row */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex flex-col gap-2">
          <span className="font-mono text-[11.5px] tracking-[0.14em]" style={{ color: 'var(--ink-link)' }}>INTELLIGENCE</span>
          <h1 className="m-0 font-heading text-[30px] font-semibold tracking-[-0.01em] leading-tight">Security Alerts</h1>
          <p className="m-0 text-sm" style={{ color: 'var(--ink-muted)' }}>
            {loading ? 'Loading…' : `${items.length} ${resLabel} · ${counts.ioc} IoC match${counts.ioc === 1 ? '' : 'es'} from threat intel · ${counts.cve} vulnerabilit${counts.cve === 1 ? 'y' : 'ies'} from NVD and OSV`}
          </p>
        </div>
        <div className="flex items-center gap-2.5">
          <button type="button" onClick={() => setPrivateOpen(true)} className="h-11 flex items-center gap-2 px-3.5 rounded-[10px] border text-[13px] font-medium" style={{ borderColor: 'var(--surf-line)', color: 'var(--ink-text)' }}>
            <Lock className="w-4 h-4" strokeWidth={1.8} style={{ color: 'var(--ink-muted)' }} />Private indicators
            {!priv.loading && !priv.loadError && <span className="px-1.5 rounded-full font-mono text-[11px]" style={{ background: 'var(--surf-raised)', color: 'var(--ink-faint)' }}>{priv.items.length}</span>}
          </button>
          <div role="group" aria-label="Resolution" className="flex gap-1 p-1 rounded-[10px] border" style={{ background: 'var(--surf-surface)', borderColor: 'var(--surf-line)' }}>
            {RESOLUTION.map(([id, label]) => (
              <button key={id} type="button" onClick={() => { setResolved(id); select(null); }} aria-pressed={resolved === id} className="h-[34px] flex items-center gap-1.5 px-3 rounded-[7px] border text-[13px]" style={seg(resolved === id)}>
                {label}{resolved === id && !loading && <span className="font-mono text-[11px]" style={{ color: 'var(--ink-faint)' }}>{items.length}</span>}
              </button>
            ))}
          </div>
          <button type="button" onClick={load} aria-label="Refresh" title="Refresh" className="w-11 h-11 flex items-center justify-center rounded-[10px] border" style={{ borderColor: 'var(--surf-line)', color: 'var(--ink-muted)' }}>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} strokeWidth={1.8} />
          </button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="w-full sm:w-[250px] h-10 box-border flex items-center gap-2 px-3 rounded-[10px] border" style={{ background: 'var(--surf-surface)', borderColor: 'var(--surf-line)' }}>
          <Search className="w-[15px] h-[15px] shrink-0" style={{ color: 'var(--ink-faint)' }} strokeWidth={1.8} />
          <input type="search" aria-label="Search alerts" placeholder="IP, domain, hash, CVE or package" value={search} onChange={e => setSearch(e.target.value)} className="flex-grow min-w-0 h-[38px] bg-transparent border-0 outline-none text-[13.5px]" style={{ color: 'var(--ink-text)' }} />
        </div>
        <div role="group" aria-label="Source" className="flex gap-1 p-[3px] rounded-[10px] border" style={{ background: 'var(--surf-surface)', borderColor: 'var(--surf-line)' }}>
          {SOURCES.map(([id, label]) => (
            <button key={id} type="button" onClick={() => setSource(id)} aria-pressed={source === id} className="h-8 flex items-center gap-1.5 px-[11px] rounded-[7px] border text-[13px]" style={seg(source === id)}>
              {label}<span className="font-mono text-[11px]" style={{ color: 'var(--ink-faint)' }}>{counts[id]}</span>
            </button>
          ))}
        </div>
        <div role="group" aria-label="Severity" className="flex flex-wrap gap-1.5">
          {SEVS.map(k => {
            const on = sevs.includes(k);
            return (
              <button key={k} type="button" onClick={() => setSevs(p => (on ? p.filter(x => x !== k) : [...p, k]))} aria-pressed={on} className="h-10 flex items-center gap-[7px] px-[11px] rounded-[10px] border text-[13px]" style={{ borderColor: on ? 'var(--accent)' : 'var(--surf-line)', background: on ? 'var(--surf-raised)' : 'var(--surf-surface)' }}>
                <span className="w-2 h-2 rounded-[2px]" style={{ background: sevFg(k) }} />{SEV_LABEL[k]}
                <span className="font-mono text-[11.5px]" style={{ color: 'var(--ink-faint)' }}>{counts[k] || 0}</span>
              </button>
            );
          })}
        </div>
        <div className="flex gap-2 ml-auto">
          <label htmlFor="f-asset" className="sr-only">Asset</label>
          <select id="f-asset" value={serverId} onChange={e => setServerId(e.target.value)} className="h-10 box-border px-2.5 rounded-[10px] border text-[13px] max-w-[180px]" style={selectStyle}>
            <option value="all">All assets</option>
            {servers.map(s => <option key={s.id} value={s.id}>{s.hostname}</option>)}
          </select>
          <label htmlFor="f-sort" className="sr-only">Sort</label>
          <select id="f-sort" value={sort} onChange={e => setSort(e.target.value)} className="h-10 box-border px-2.5 rounded-[10px] border text-[13px]" style={selectStyle}>
            {Object.entries(SORT_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
      </div>

      {notice && (
        <div role={notice.kind === 'error' ? 'alert' : 'status'} className="flex items-start justify-between gap-3 px-4 py-3 rounded-[10px] border text-[13px]" style={{ borderColor: notice.kind === 'error' ? 'var(--sev-crit-fg)' : 'var(--sev-good-fg)', background: notice.kind === 'error' ? 'var(--sev-crit-bg)' : 'var(--sev-good-bg)' }}>
          <span>{notice.text}</span>
          <button type="button" onClick={() => setNotice(null)} aria-label="Dismiss" className="shrink-0" style={{ color: 'var(--ink-muted)' }}><X className="w-4 h-4" /></button>
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_480px] gap-5 xl:h-[calc(100vh-290px)] xl:min-h-[560px]">
        <section aria-label="Alerts" className="min-h-[520px] rounded-[14px] border flex flex-col overflow-hidden" style={{ background: 'var(--surf-surface)', borderColor: 'var(--surf-line)' }}>
          {/* List header, or the bulk bar when anything is checked */}
          {checked.size === 0 ? (
            <div className="h-[46px] box-border flex items-center gap-3 pr-4 pl-1 border-b shrink-0" style={{ borderColor: 'var(--surf-line)' }}>
              <label className="w-10 h-11 flex items-center justify-center cursor-pointer">
                <input type="checkbox" aria-label="Select all visible" checked={allChecked} onChange={toggleAll} disabled={!visible.length} className="m-0 w-[15px] h-[15px]" style={{ accentColor: 'var(--accent)' }} />
              </label>
              <span className="font-mono text-[10.5px] tracking-[0.12em]" style={{ color: 'var(--ink-faint)' }}>{visible.length} ALERT{visible.length === 1 ? '' : 'S'}</span>
              <span className="ml-auto text-xs hidden sm:inline" style={{ color: 'var(--ink-faint)' }}>Sorted by {SORT_LABEL[sort].toLowerCase()}</span>
            </div>
          ) : (
            <div className="h-[46px] box-border flex items-center gap-2.5 pr-3 pl-1 border-b shrink-0" style={{ borderColor: 'var(--accent)', background: 'var(--surf-raised)' }}>
              <label className="w-10 h-11 flex items-center justify-center cursor-pointer">
                <input type="checkbox" aria-label="Select all visible" checked={allChecked} onChange={toggleAll} className="m-0 w-[15px] h-[15px]" style={{ accentColor: 'var(--accent)' }} />
              </label>
              <span className="text-[13.5px] font-semibold">{checked.size} selected</span>
              <span className="flex-grow" />
              {checkedItems.some(i => !i.resolved) && (
                <button type="button" disabled={busy} onClick={() => resolveItems(checkedItems.filter(i => !i.resolved))} className="h-[34px] px-3 rounded-lg text-[13px] font-semibold disabled:opacity-50" style={{ background: 'var(--accent)', color: '#0A0A0A' }}>
                  {busy ? 'Resolving…' : 'Resolve'}
                </button>
              )}
              <button type="button" onClick={() => setCaseItems(checkedItems)} className="h-[34px] px-3 rounded-lg border text-[13px] font-medium" style={{ borderColor: 'var(--surf-line-strong)', color: 'var(--ink-text)' }}>Create case</button>
              <button type="button" onClick={() => openChatWithAlert(checkedItems.map(i => i.id), checkedItems[0].kind)} disabled={new Set(checkedItems.map(i => i.kind)).size > 1} title={new Set(checkedItems.map(i => i.kind)).size > 1 ? 'Pick alerts of one type to discuss together' : 'Discuss with AI'} className="h-[34px] px-3 rounded-lg border text-[13px] font-medium disabled:opacity-40 hidden sm:block" style={{ borderColor: 'var(--surf-line-strong)', color: 'var(--ink-text)' }}>Discuss</button>
              <button type="button" onClick={() => setChecked(new Set())} className="h-[34px] px-2.5 rounded-lg text-[13px]" style={{ color: 'var(--ink-muted)' }}>Clear</button>
            </div>
          )}

          {/* Today's NVD feed as a one-line strip */}
          {nvd && (
            <div className="border-b shrink-0" style={{ borderColor: 'var(--surf-line)' }}>
              <div className="flex items-center gap-2.5 min-h-[42px] px-4 text-[12.5px]" style={{ color: 'var(--ink-muted)' }}>
                <span className="font-mono text-[10.5px] tracking-[0.1em] shrink-0" style={{ color: 'var(--ink-faint)' }}>NVD TODAY</span>
                <span className="truncate">
                  {nvd.total_today} CVE{nvd.total_today === 1 ? '' : 's'} published · {nvd.high_critical.length} high or critical · {nvdMatches ? `${nvdMatches} match your packages` : 'none match your packages yet'}
                </span>
                {nvd.high_critical.length > 0 && (
                  <button type="button" onClick={() => setNvdOpen(o => !o)} aria-expanded={nvdOpen} className="ml-auto shrink-0 flex items-center gap-1 py-1.5" style={{ color: 'var(--ink-link)' }}>
                    {nvdOpen ? 'Hide feed' : 'Show feed'}{nvdOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                  </button>
                )}
              </div>
              {nvdOpen && (
                <div className="max-h-56 overflow-y-auto border-t" style={{ borderColor: 'var(--surf-line)' }}>
                  {nvd.high_critical.map(n => {
                    const k = n.severity === 'CRITICAL' ? 'crit' : 'high';
                    return (
                      <a key={n.cve_id} href={`https://nvd.nist.gov/vuln/detail/${n.cve_id}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 px-4 py-2 border-b last:border-b-0 no-underline" style={{ borderColor: 'var(--surf-line)', color: 'var(--ink-text)' }}>
                        <SevChip sev={k} />
                        <span className="font-mono text-[12.5px] shrink-0" style={{ color: 'var(--ink-link)' }}>{n.cve_id}</span>
                        <span className="text-xs truncate flex-grow" style={{ color: 'var(--ink-faint)' }}>{n.description}</span>
                        {n.cvss_score != null && <span className="font-mono text-xs shrink-0">{Number(n.cvss_score).toFixed(1)}</span>}
                        <ExternalLink className="w-3 h-3 shrink-0" style={{ color: 'var(--ink-faint)' }} />
                      </a>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Private-IP false positives */}
          {source !== 'cve' && fpItems.length > 0 && (
            <div role="note" className="flex flex-wrap items-center gap-3 mx-3 mt-2.5 mb-0.5 px-3 py-2.5 rounded-[10px] border border-dashed text-[12.5px] leading-snug shrink-0" style={{ borderColor: 'var(--surf-line-strong)', color: 'var(--ink-muted)' }}>
              <Info className="w-4 h-4 shrink-0" strokeWidth={1.8} />
              <span className="flex-grow min-w-[200px]">
                {fpItems.length} alert{fpItems.length === 1 ? ' is' : 's are'} for private IP addresses (10.x, 192.168.x). Those can’t be external threats.
              </span>
              <button type="button" onClick={() => setChecked(new Set(fpItems.map(i => i.key)))} className="h-8 px-3 rounded-lg text-[12.5px] font-medium shrink-0" style={{ color: 'var(--ink-muted)' }}>Select</button>
              <button type="button" onClick={resolvePrivate} disabled={busy} className="h-8 px-3 rounded-lg border text-[12.5px] font-medium shrink-0 disabled:opacity-50" style={{ borderColor: 'var(--surf-line-strong)', color: 'var(--ink-text)' }}>Resolve all</button>
            </div>
          )}

          <div className="flex-grow min-h-0 overflow-y-auto py-1.5">
            {loading && items.length === 0 && <div className="py-10 text-center text-sm" style={{ color: 'var(--ink-faint)' }}>Loading alerts…</div>}
            {!loading && visible.length === 0 && (
              <div className="py-10 px-6 text-center text-sm" style={{ color: 'var(--ink-faint)' }}>
                {items.length ? 'No alerts match these filters.' : resolved === '0' ? 'Nothing unresolved. Alerts appear when agent observations match threat intelligence or an installed package matches a CVE.' : 'No alerts.'}
              </div>
            )}
            {visible.map(i => {
              const on = i.key === selectedKey;
              const ch = checked.has(i.key);
              return (
                <div key={i.key} className="relative h-14 flex items-center" style={{ background: on ? 'var(--surf-raised)' : ch ? 'var(--surf-bg)' : 'transparent', opacity: i.resolved && resolved === 'all' ? 0.6 : 1 }}>
                  <label className="w-11 h-14 shrink-0 flex items-center justify-center cursor-pointer">
                    <input type="checkbox" aria-label={`Select ${i.title}`} checked={ch} onChange={() => toggle(i.key)} className="m-0 w-[15px] h-[15px]" style={{ accentColor: 'var(--accent)' }} />
                  </label>
                  <button type="button" onClick={() => select(on ? null : i.key)} aria-pressed={on} className="flex-grow min-w-0 h-14 flex items-center gap-3 pr-4 border-b text-left" style={{ borderColor: 'var(--surf-line)' }}>
                    <span className="w-[84px] shrink-0"><SevChip sev={i.sev} /></span>
                    <span className="w-[34px] shrink-0 font-mono text-[10.5px] tracking-[0.08em]" style={{ color: 'var(--ink-faint)' }}>{i.kind.toUpperCase()}</span>
                    <span className="flex-grow min-w-0 flex flex-col gap-[3px]">
                      <span className="text-sm font-medium truncate">{i.title}</span>
                      <span className="font-mono text-[11.5px] truncate" style={{ color: 'var(--ink-faint)' }}>{i.meta}</span>
                    </span>
                    {i.kind === 'ioc' && isPrivateAlert(i.raw) && <span className="px-1.5 py-0.5 rounded border font-mono text-[10px] tracking-[0.08em] shrink-0" style={{ borderColor: 'var(--accent)', color: 'var(--ink-text)' }}>PRIVATE</span>}
                    {i.resolved && resolved === 'all' && <span className="font-mono text-[10px] tracking-[0.08em] shrink-0" style={{ color: 'var(--sev-good-fg)' }}>RESOLVED</span>}
                    <VerdictBadge verdict={verdicts[`${i.kind}:${i.id}`]} />
                    <span className="font-mono text-[11.5px] shrink-0" style={{ color: 'var(--ink-faint)' }}>{ago(i.detected_at)}</span>
                  </button>
                  {on && <span aria-hidden="true" className="absolute right-0 top-4 w-px h-6" style={{ background: 'var(--accent)' }} />}
                </div>
              );
            })}
          </div>
        </section>

        <AlertDetail
          item={selected}
          assetCount={selected?.kind === 'ioc' ? iocSpread[selected.raw.ioc_value]?.size || 1 : 1}
          busy={busy}
          onClose={() => select(null)}
          onResolve={() => selected && resolveItems([selected])}
          onCreateCase={() => selected && setCaseItems([selected])}
          onDiscuss={() => selected && openChatWithAlert([selected.id], selected.kind)}
          verdict={selected ? verdicts[`${selected.kind}:${selected.id}`] : undefined}
          canRate={canRate}
          onVerdictChange={(v) => selected && setVerdict(`${selected.kind}:${selected.id}`, v)}
        />
      </div>

      <CreateCaseDrawer open={!!caseItems} items={caseItems || []} onClose={() => setCaseItems(null)} />
      <PrivateIndicatorsDrawer open={privateOpen} onClose={() => setPrivateOpen(false)} iocs={priv} canManage={canManage} />
    </div>
  );
};

export default SecurityAlerts;
